import { useNavigate, Link } from "react-router-dom"
import { useCallback, useMemo } from "react"
import { Box, Button, Typography, useTheme } from "@mui/material"
import _ from "lodash"
import { Head } from "components/Head"
import { MotionDiv } from "components/motions"
import { Image } from "components/Image"
import { getFeatureDetail } from "utils/feature"
import { useVenue } from "providers/venue"
import { useTranslation } from "providers/i18n"
import { useExtension } from "hooks/extension/useExtension"
import { EXTENSION_NAME } from "extensions/em-benchasiri-park/constant"
import { useEmBenchasiri } from "extensions/em-benchasiri-park/hooks/useEmBenchasiri"
import { BenchasiriEventCard } from "extensions/em-benchasiri-park/components/Card"

export const EMBenchasiriMobilePage = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { events, findFeatureById } = useVenue()
  const { extensionConfig } = useExtension()
  const navigate = useNavigate()
  const benchasiriConfig = useMemo(
    () =>
      extensionConfig?.[EXTENSION_NAME]?.options || {
        location: { id: null },
      },
    [extensionConfig]
  )

  const locationFeature = useMemo(
    () => findFeatureById(benchasiriConfig?.location?.id),
    [benchasiriConfig?.location?.id, findFeatureById]
  )

  const handleClickViewLocation = useCallback(
    () => navigate(`/maps/place/${locationFeature.id}`),
    [locationFeature, navigate]
  )

  const {
    title,
    description,
    events: benchasiriEvents,
  } = useEmBenchasiri(locationFeature, events, benchasiriConfig)

  const pageStyleOverrides =
    theme.components[`EmBenchasiriPage`]?.styleOverrides || {}

  const benchasiriImage = useMemo(
    () => _.get(theme, "assets.benchasiriPark.url"),
    [theme]
  )

  const {
    root,
    image,
    titleText,
    descriptionWrapper,
    descriptionText,
    ctaWrapper,
    ctaButton,
    eventSection,
    eventItemContainer,
    titleWrapper,
  } = pageStyleOverrides

  return (
    <>
      <Head title={t("Benchasiri Park")} />
      <MotionDiv
        sx={root}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        <Image sx={image} src={benchasiriImage} />

        <Box sx={titleWrapper}>
          <Typography variant="h1" sx={titleText}>
            {title}
          </Typography>
        </Box>
        <Box sx={descriptionWrapper}>
          <Typography variant="body1" sx={descriptionText}>
            {description}
          </Typography>
        </Box>
        <Box sx={ctaWrapper}>
          {locationFeature ? (
            <Button
              variant="contained"
              sx={ctaButton}
              onClick={handleClickViewLocation}
            >
              {t("View Location")}
            </Button>
          ) : null}
        </Box>
        <Box sx={eventSection}>
          {benchasiriEvents.map((event) => {
            const { name, logo, startTime, endTime, id } =
              getFeatureDetail(event)
            return (
              <Box sx={eventItemContainer} key={event.id}>
                <BenchasiriEventCard
                  imageSrc={logo}
                  title={t(name)}
                  subtitle={
                    startTime && endTime ? `${startTime} - ${endTime}` : ""
                  }
                  component={Link}
                  to={`/events/${id}`}
                />
              </Box>
            )
          })}
        </Box>
      </MotionDiv>
    </>
  )
}
